<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <div
    v-else-if="!computedCarouselProps.is_removed"
    :style="isEditMode ? omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight') : undefined"
  >
    <EditableComponent
      v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :discard="shouldShowDiscardOption"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :remove="componentPermissions.canRemove"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :stylable="!usedInSectionId"
      @discard="discard()"
      @duplicate="duplicate(id)"
      @edit="edit()"
      @remove="removeComponent(id, usedInSectionId)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <RawCarousel
        v-if="computedCarouselProps.data?.images?.length"
        :data="computedCarouselProps.data"
        :style="style"
        v-bind="attrs"
      />
      <EditCarousel v-else v-bind="attrs" />
    </EditableComponent>

    <RawCarousel
      v-else-if="computedCarouselProps.data?.images?.length"
      :data="computedCarouselProps.data"
      :style="style"
      v-bind="attrs"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, onMounted, useAttrs, type ComputedRef, type CSSProperties } from 'vue'
import { useI18n } from 'vue-i18n'
import { omit } from 'lodash-es'

import { ComponentTypes, type ComponentNode, type CarouselProps } from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditCarousel from './Carousel.edit.vue'
import RawCarousel from './Carousel.raw.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import useConfirmation from '@lib/composables/useConfirmation'
import { type CanvasRetrieve } from '@openapi'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import { discardSectionComponentOverride, shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'

const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: CarouselProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties]
}>()

const { t } = useI18n()

const {
  activeCanvas,
  componentEditMode,
  componentSelectedMode,
  duplicate,
  isEditMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponent,
  setComponentSelectedMode,
  setComponentEditMode,
  sectionComponentOverrides,
  setComponentSettingsMode,
} = useCanvas()
const { discardOverridesConfirmationOptions, getComponentOverrides } = useCanvasSectionOverrides()
const { id, data, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})
const confirmation = useConfirmation()

const computedCarouselProps = getComponentOverrides(data, id, usedInSectionId) as ComputedRef<{
  data: CarouselProps
  is_removed: boolean
}>

const shouldShowDiscardOption = computed(() => {
  return !!(props.usedInSectionId && sectionComponentOverrides.value?.[props.usedInSectionId]?.[props.id])
})

const discard = () => {
  confirmation.warning(
    discardOverridesConfirmationOptions(t, () => {
      try {
        if (!activeCanvas.value?.id) {
          throw new Error('Canvas ID is not defined')
        }
        const updatedOverrides = discardSectionComponentOverride(
          props.usedInSectionId,
          props.id,
          sectionComponentOverrides.value,
        )

        // update section component overrides
        sectionComponentOverrides.value = updatedOverrides
      } catch (error) {
        console.error(error)
        return
      }
    }),
  )
}

function edit() {
  setComponentEditMode(ComponentTypes.Carousel, props.id, props.usedInSectionId)
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    edit()
  }
})
</script>
